import { useState } from 'react';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import VisibleLoading from '@/components/utilities/VisibleLoading';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { getImagePath } from '@/utils/images';

const TAG_COLORS = {
  online: 'bg-brand-400',
  offline: 'bg-[#9f5cf0]'
};

function SpeakerInfo({ webinar }) {
  return (
    <div className="flex items-center gap-3">
      {webinar.speakerAvatar?.url && (
        <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-full">
          <Image
            src={getImagePath(webinar.speakerAvatar)}
            alt={webinar.speakerName || ''}
            fill
            className="object-cover"
          />
        </div>
      )}
      <div>
        <div className="txt-lg font-medium text-black">{webinar.speakerName}</div>
        <div className="txt-md text-grey-700">{webinar.speakerTitle}</div>
      </div>
    </div>
  );
}

function TagBadge({ tag }) {
  if (!tag) return null;

  return (
    <span
      className={`inline-flex w-fit items-center rounded-md p-1.5 text-sm font-semibold tracking-tight text-white uppercase ${TAG_COLORS[tag] || 'bg-brand-400'}`}
    >
      {tag}
    </span>
  );
}

function UpcomingWebinarCard({ webinar, labels }) {
  const ctaLabel = webinar.ctaLabel || 'Přihlásit se na webinář';
  const [isOpen, setIsOpen] = useState(false);
  const [formState, setFormState] = useState('idle');
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '' });
  const [honeypot, setHoneypot] = useState('');

  const handleOpenChange = (open) => {
    setIsOpen(open);
    if (open) {
      setFormState('idle');
      setFormData({ firstName: '', lastName: '', email: '' });
      setHoneypot('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormState('loading');

    try {
      const shouldSubscribe = webinar.subscribe ?? true;
      const subscribeLists = webinar.subscribeLists || 'newsletters';

      const identifyData = {
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName
      };
      if (shouldSubscribe) {
        identifyData.subscribe = subscribeLists
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      }

      const tagData = {
        name: 'webinar',
        data: {
          id: webinar.tagName || webinar.id,
          date: {
            datatype: 'datetime',
            value: new Date().toISOString()
          }
        }
      };

      fetch('/api/webinar-registration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identify: identifyData, tag: tagData, hp: honeypot })
      });

      if (window.lhi) {
        window.lhi('identify', identifyData);
        window.lhi('addTag', tagData);
      }

      setFormState('success');
    } catch {
      setFormState('error');
    }
  };

  const formattedDate = webinar.date
    ? new Date(webinar.date).toLocaleString('cs-CZ', {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: 'Europe/Prague'
      })
    : '';

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {/* Card */}
      <div className="flex h-full flex-col justify-between rounded-2xl bg-white p-4 pb-6 shadow-[0px_11px_24px_0px_rgba(0,0,0,0.05),0px_43px_43px_0px_rgba(0,0,0,0.06)]">
        <div className="flex flex-1 flex-col gap-6">
          <SpeakerInfo webinar={webinar} />
          <TagBadge tag={webinar.tag} />
          <h6 className="text-black">{webinar.name}</h6>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <Button onClick={() => handleOpenChange(true)}>{ctaLabel}</Button>
          <DateDisplay date={formattedDate} />
        </div>
      </div>

      {/* Modal via portal */}
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogTitle className="sr-only">{webinar.name}</DialogTitle>
        <DialogDescription className="sr-only">Registrace na webinář</DialogDescription>

        <div className="flex flex-col gap-6">
          <SpeakerInfo webinar={webinar} />
          <TagBadge tag={webinar.tag} />
          <h6 className="text-black">{webinar.name}</h6>

          {webinar.description && (
            <div className="txt-md text-grey-700 [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5">
              <Markdown rehypePlugins={[rehypeRaw]}>{webinar.description}</Markdown>
            </div>
          )}

          {formState === 'success' ? (
            <div className="rounded-lg bg-green-50 p-4 text-center text-green-700">
              <p className="font-medium">{labels.successMessage}</p>
              <p className="mt-2 text-sm">{labels.successNote}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <input
                name="company"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                autoComplete="off"
                tabIndex={-1}
                aria-hidden="true"
                className="absolute h-0 w-0 overflow-hidden opacity-0"
              />
              <Input
                name="given-name"
                autoComplete="given-name"
                placeholder={labels.placeholderFirstName}
                value={formData.firstName}
                onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
                required
                className="border-brand-400 focus-visible:border-brand-400"
              />
              <Input
                name="family-name"
                autoComplete="family-name"
                placeholder={labels.placeholderLastName}
                value={formData.lastName}
                onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
                required
                className="border-brand-400 focus-visible:border-brand-400"
              />
              <Input
                type="email"
                name="email"
                autoComplete="email"
                placeholder={labels.placeholderEmail}
                value={formData.email}
                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                required
                className="border-brand-400 focus-visible:border-brand-400"
              />

              {formState === 'error' && <div className="txt-sm text-red-600">{labels.errorMessage}</div>}

              <div className="flex items-center justify-between pt-2">
                <Button type="submit" disabled={formState === 'loading'}>
                  {formState === 'loading' ? labels.loadingMessage : ctaLabel}
                </Button>
                <DateDisplay date={formattedDate} />
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DateDisplay({ date }) {
  if (!date) return null;

  return (
    <div className="flex items-center gap-2">
      <Icon type="calendar" width={24} height={24} color="black" />
      <span className="txt-md font-semibold tracking-tight text-black uppercase">{date}</span>
    </div>
  );
}

const DEFAULTS = {
  placeholderFirstName: 'Vaše jméno',
  placeholderLastName: 'Vaše příjmení',
  placeholderEmail: 'Vaše e-mailová adresa',
  successMessage: 'Registrace proběhla úspěšně!',
  successNote: 'Nyní prosím potvrďte e-mail, který jsme vám zaslali. Poté vám dorazí odkaz na webinář.',
  errorMessage: 'Něco se pokazilo. Zkuste to znovu.',
  loadingMessage: 'Odesílám...'
};

const UpcomingWebinarsBlock = ({
  hidden,
  upcomingWebinarsTitle,
  upcomingWebinars,
  placeholderFirstName,
  placeholderLastName,
  placeholderEmail,
  successMessage,
  successNote,
  errorMessage,
  loadingMessage
}) => {
  const webinars = upcomingWebinars || [];

  if (hidden || webinars.length === 0) return null;

  const labels = {
    placeholderFirstName: placeholderFirstName || DEFAULTS.placeholderFirstName,
    placeholderLastName: placeholderLastName || DEFAULTS.placeholderLastName,
    placeholderEmail: placeholderEmail || DEFAULTS.placeholderEmail,
    successMessage: successMessage || DEFAULTS.successMessage,
    successNote: successNote || DEFAULTS.successNote,
    errorMessage: errorMessage || DEFAULTS.errorMessage,
    loadingMessage: loadingMessage || DEFAULTS.loadingMessage
  };

  return (
    <div className="mx-auto mt-10 mb-20 w-[90vw] xl:mb-32 xl:w-(--container-width)">
      {upcomingWebinarsTitle && (
        <VisibleLoading delay={100}>
          {({ ref, className }) => (
            <h2 ref={ref} className={`mb-12 text-center ${className || ''}`.trim()}>
              {upcomingWebinarsTitle}
            </h2>
          )}
        </VisibleLoading>
      )}

      <div className="-mx-3 flex flex-wrap justify-center gap-y-6">
        {webinars.map((webinar, index) => (
          <VisibleLoading key={webinar.id} delay={(index + 1) * 200}>
            {({ ref, className }) => (
              <div ref={ref} className={`basis-full px-3 xl:basis-1/3 ${className || ''}`.trim()}>
                <UpcomingWebinarCard webinar={webinar} labels={labels} />
              </div>
            )}
          </VisibleLoading>
        ))}
      </div>
    </div>
  );
};

export default UpcomingWebinarsBlock;
